import axios from 'axios';

const BACKEND_URL = '';
const API_URL = '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Check if this is an engineer endpoint
  const isEngineerEndpoint = config.url?.includes('engineer');
  if (isEngineerEndpoint) {
    const engineerToken = localStorage.getItem('engineer_token');
    if (engineerToken) {
      config.headers.Authorization = `Bearer ${engineerToken}`;
    }
  } else {
    const portalToken = localStorage.getItem('portal_token');
    const token = localStorage.getItem('token');
    // Pick the token by the app the user is actually in (page path), NOT by
    // whether the request URL contains "portal". Staff routes like /portal-chat
    // and endpoints like /customers/{id}/portal-messages also contain that
    // substring and MUST use the staff token — otherwise a leftover portal_token
    // in localStorage hijacks staff requests and the staff Portal Chat silently
    // 401s / shows empty.
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const isCustomerPortalPage = path === '/portal' || path.startsWith('/portal/');
    if (isCustomerPortalPage && portalToken) {
      config.headers.Authorization = `Bearer ${portalToken}`;
    } else if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (portalToken) {
      // Customer-only session (no staff token present).
      config.headers.Authorization = `Bearer ${portalToken}`;
    }
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Don't redirect for auth endpoints (login/register) 
      if (url.includes('/auth/login') || url.includes('/auth/register')) {
        return Promise.reject(error);
      }
      // Engineer endpoints redirect to engineer login
      if (url.includes('engineer')) {
        localStorage.removeItem('engineer_token');
        localStorage.removeItem('engineer_user');
        if (window.location.pathname.startsWith('/engineer')) {
          window.location.href = '/engineer-login';
        }
        return Promise.reject(error);
      }
      // Customer portal pages: don't redirect to the admin login on 401.
      // Match by the actual page path, not a "portal" substring in the URL
      // (staff routes /portal-chat, /portal-accounts contain it too).
      const path = window.location.pathname;
      const isCustomerPortalPage = path === '/portal' || path.startsWith('/portal/');
      if (isCustomerPortalPage) {
        return Promise.reject(error);
      }
      // Admin redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  getMyEnaUsername: () => api.get('/auth/me/ena-username'),
  updateMyEnaUsername: (ena_sub_installer_username) =>
    api.put('/auth/me/ena-username', { ena_sub_installer_username }),
};

// Customers API
export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  updateStatus: (id, data) => api.patch(`/customers/${id}/status`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  bulkStatus: (data) => api.post('/customers/bulk/status', data),
  bulkAssign: (data) => api.post('/customers/bulk/assign', data),
  bulkEmail: (data) => api.post('/customers/bulk/email', data),
  bulkEdit: (data) => api.post('/customers/bulk/edit', data),
  listJobs: (id) => api.get(`/customers/${id}/jobs`),
  createJob: (id, data) => api.post(`/customers/${id}/jobs`, data),
  renameJob: (id, data) => api.patch(`/customers/${id}/job-label`, data),
  portalPreview: (id) => api.get(`/customers/${id}/portal-preview`),
};

// Stage Payments API
export const stagePaymentsAPI = {
  list: (customerId) => api.get(`/customers/${customerId}/stage-payments`),
  create: (customerId, data) => api.post(`/customers/${customerId}/stage-payments`, data),
  update: (customerId, spId, data) => api.patch(`/customers/${customerId}/stage-payments/${spId}`, data),
  remove: (customerId, spId) => api.delete(`/customers/${customerId}/stage-payments/${spId}`),
};

// HR API
export const hrAPI = {
  permissions: () => api.get('/hr/permissions'),
  myProfile: () => api.get('/hr/me'),
  updateMyProfile: (data) => api.patch('/hr/me', data),
  listEmployees: (includeTerminated = false) => api.get('/hr/employees', { params: { include_terminated: includeTerminated } }),
  getEmployee: (id) => api.get(`/hr/employees/${id}`),
  createEmployee: (data) => api.post('/hr/employees', data),
  updateEmployee: (id, data) => api.patch(`/hr/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/hr/employees/${id}`),
  linkEngineer: (id, data) => api.post(`/hr/employees/${id}/link-engineer`, data),
  linkUser: (id, linkedUserId) => api.post(`/hr/employees/${id}/link-user`, { linked_user_id: linkedUserId }),
  employeeTimesheets: (id, days = 30) => api.get(`/hr/employees/${id}/timesheets`, { params: { days } }),
  engineerLookup: () => api.get('/hr/lookups/engineers'),
  userLookup: () => api.get('/hr/lookups/users'),
  summary: (id) => api.get(`/hr/employees/${id}/summary`),

  // Documents
  documentCategories: () => api.get('/hr/documents/categories'),
  listDocuments: (employeeId, category = null) =>
    api.get(`/hr/employees/${employeeId}/documents`, { params: category ? { category } : {} }),
  uploadDocument: (employeeId, file, category = 'other', notes = '') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    fd.append('notes', notes || '');
    return api.post(`/hr/employees/${employeeId}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadDocument: (docId) => api.get(`/hr/documents/${docId}/download`, { responseType: 'blob' }),
  deleteDocument: (docId) => api.delete(`/hr/documents/${docId}`),

  // Leave
  listLeave: (employeeId) => api.get(`/hr/employees/${employeeId}/leave`),
  pendingLeave: () => api.get('/hr/leave/pending'),
  createLeave: (employeeId, data) => api.post(`/hr/employees/${employeeId}/leave`, data),
  updateLeave: (leaveId, data) => api.patch(`/hr/leave/${leaveId}`, data),
  deleteLeave: (leaveId) => api.delete(`/hr/leave/${leaveId}`),

  // Performance
  listReviews: (employeeId) => api.get(`/hr/employees/${employeeId}/performance-reviews`),
  createReview: (employeeId, data) => api.post(`/hr/employees/${employeeId}/performance-reviews`, data),
  updateReview: (reviewId, data) => api.patch(`/hr/performance-reviews/${reviewId}`, data),
  deleteReview: (reviewId) => api.delete(`/hr/performance-reviews/${reviewId}`),

  // Training
  listTraining: (employeeId) => api.get(`/hr/employees/${employeeId}/training`),
  createTraining: (employeeId, data) => api.post(`/hr/employees/${employeeId}/training`, data),
  updateTraining: (trainingId, data) => api.patch(`/hr/training/${trainingId}`, data),
  deleteTraining: (trainingId) => api.delete(`/hr/training/${trainingId}`),

  // Disciplinaries (HR Admin only)
  listDisciplinaries: (employeeId) => api.get(`/hr/employees/${employeeId}/disciplinaries`),
  createDisciplinary: (employeeId, data) => api.post(`/hr/employees/${employeeId}/disciplinaries`, data),
  updateDisciplinary: (discId, data) => api.patch(`/hr/disciplinaries/${discId}`, data),
  deleteDisciplinary: (discId) => api.delete(`/hr/disciplinaries/${discId}`),
};

// Documents API
export const documentsAPI = {
  upload: (customerId, file, displayName, docTag) => {
    const formData = new FormData();
    formData.append('file', file);
    if (displayName) formData.append('display_name', displayName);
    if (docTag) formData.append('doc_tag', docTag);
    return api.post(`/customers/${customerId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  suggestImageName: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/ai/suggest-image-name`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (customerId, documentId) => 
    api.get(`/customers/${customerId}/documents/${documentId}`, {
      responseType: 'blob',
    }),
  preview: (customerId, documentId) => 
    api.get(`/customers/${customerId}/documents/${documentId}/preview`, {
      responseType: 'blob',
    }),
  updateVisibility: (customerId, documentId, visible) =>
    api.patch(`/customers/${customerId}/documents/${documentId}/visibility`, { visible }),
  updateFolder: (customerId, documentId, folder) =>
    api.patch(`/customers/${customerId}/documents/${documentId}/folder`, { folder }),
  updateTag: (customerId, documentId, tag) =>
    api.patch(`/customers/${customerId}/documents/${documentId}/tag`, { tag }),
  delete: (customerId, documentId) => 
    api.delete(`/customers/${customerId}/documents/${documentId}`),
};

// Yeastar PBX API
export const yeastarAPI = {
  getConfig: () => api.get('/yeastar/config'),
  saveConfig: (data) => api.post('/yeastar/config', data),
  regenerateKey: () => api.post('/yeastar/regenerate-key'),
  xmlTemplate: () => api.get('/yeastar/xml-template', { responseType: 'text' }),
  getExtensionMap: () => api.get('/yeastar/extension-map'),
  setExtension: (userId, extension) => api.post('/yeastar/extension-map', { user_id: userId, extension }),
  analytics: (days = 30) => api.get(`/yeastar/analytics?days=${days}`),
  today: () => api.get('/yeastar/today'),
  callLogs: (params = {}) => api.get('/yeastar/call-logs', { params }),
  inboundLog: () => api.get('/yeastar/inbound-log'),
  selfTest: () => api.get('/yeastar/self-test'),
  test: () => api.post('/yeastar/test'),
  configureWebhook: () => api.post('/yeastar/configure-webhook'),
  getExtensions: () => api.get('/yeastar/extensions'),
  backfill: (days) => api.post('/yeastar/backfill', { days }),
  activeCall: () => api.get('/yeastar/active-call'),
  recordingControl: (action, channelId) => api.post(`/yeastar/calls/recording/${action}`, { channel_id: channelId }),
  dial: (number) => api.post('/yeastar/dial', { number }),
  egressIp: () => api.get('/yeastar/egress-ip'),
};


// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivity: () => api.get('/dashboard/recent-activity'),
  getMyCustomers: () => api.get('/dashboard/my-customers'),
  getWinLoss: () => api.get('/dashboard/win-loss'),
};

// Stages API
export const stagesAPI = {
  getAll: () => api.get('/stages'),
};

// Engineers API
export const engineersAPI = {
  getAll: (activeOnly = true) => api.get('/engineers', { params: { active_only: activeOnly } }),
  getById: (id) => api.get(`/engineers/${id}`),
  create: (data) => api.post('/engineers', data),
  update: (id, data) => api.put(`/engineers/${id}`, data),
  delete: (id) => api.delete(`/engineers/${id}`),
};

// Installations API
export const installationsAPI = {
  getAll: (params) => api.get('/installations', { params }),
  getById: (id) => api.get(`/installations/${id}`),
  create: (data) => api.post('/installations', data),
  update: (id, data) => api.put(`/installations/${id}`, data),
  delete: (id) => api.delete(`/installations/${id}`),
  getMonth: (year, month) => api.get(`/calendar/month/${year}/${month}`),
};

// Email Templates API
export const emailTemplatesAPI = {
  getAll: () => api.get('/email-templates'),
  getByTrigger: (trigger) => api.get(`/email-templates/${trigger}`),
  create: (data) => api.post('/email-templates', data),
  update: (trigger, data) => api.put(`/email-templates/${trigger}`, data),
  delete: (trigger) => api.delete(`/email-templates/${trigger}`),
};

// Export API
export const exportAPI = {
  customersCSV: (params = {}) => api.get('/customers/export/csv', {
    params,
    responseType: 'blob',
  }),
};

// Customer-list helpers
export const customersFilterAPI = {
  counts: (params = {}) => api.get('/customers/filter-counts', { params }),
};

// SharePoint API
export const sharepointAPI = {
  syncCustomer: (customerId) => api.post(`/customers/${customerId}/sharepoint/sync`),
};

// Admin API (User Management)
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/create-user', data),
  createEngineer: (data) => api.post('/admin/create-engineer', data),
  createPortalAccount: (data) => api.post('/admin/create-portal-account', data),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  makeAdmin: (userId) => api.post(`/admin/users/${userId}/make-admin`),
  makeMeAdmin: () => api.post('/admin/make-me-admin'),
  resetUserPassword: (userId, data) => api.post(`/admin/users/${userId}/reset-password`, data),
  resetEngineerPassword: (accountId, data) => api.post(`/admin/engineer-accounts/${accountId}/reset-password`, data),
  resetPortalPassword: (accountId, data) => api.post(`/admin/portal-accounts/${accountId}/reset-password`, data),
  getEngineerAccounts: () => api.get('/engineer-accounts'),
  approveEngineer: (id) => api.post(`/engineer-accounts/${id}/approve`),
  rejectEngineer: (id) => api.post(`/engineer-accounts/${id}/reject`),
  getPortalAccounts: () => api.get('/admin/portal-accounts'),
  bulkPortalAction: (data) => api.post('/admin/portal-accounts/bulk', data),
  listBackups: () => api.get('/admin/backups'),
  runBackupNow: () => api.post('/admin/backups/run-now'),
  previewBackup: (backupId) => api.get(`/admin/backups/${backupId}/preview`),
  restoreBackup: (backupId, collections, confirm = 'RESTORE') =>
    api.post(`/admin/backups/${backupId}/restore`, { collections, confirm }),
  timeSheetsWeekly: (weeks = 6) => api.get('/admin/time-sheets/weekly', { params: { weeks } }),
  timeSheetsShifts: (params) => api.get('/admin/engineer-shifts', { params }),
  timeSheetsApprove: (shiftId, decision, note = '') =>
    api.post(`/admin/time-sheets/${shiftId}/approve`, { decision, note }),
  timeSheetsCsvUrl: (days = 30) => `${API_URL}/admin/time-sheets/export.csv?days=${days}`,
};

// Users API (for assignments)
export const usersAPI = {
  getAll: () => api.get('/admin/users'),
};

// Password Reset API
export const passwordResetAPI = {
  requestReset: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

// PDF/Quote API
export const quoteAPI = {
  generatePDF: (customerId) => api.get(`/customers/${customerId}/quote/pdf`, { responseType: 'blob' }),
};

// Xero API
export const xeroAPI = {
  getSettings: () => api.get('/settings/xero'),
  saveSettings: (data) => api.post('/settings/xero', data),
  sendInvoice: (customerId) => api.post(`/customers/${customerId}/invoice/xero`),
  getCustomerInvoices: (customerId) => api.get(`/customers/${customerId}/xero/invoices`),
  syncPayments: (customerId) => api.post(`/customers/${customerId}/xero/sync-payments`),
  deleteInvoice: (invoiceId, voidInXero = true) => api.delete(`/xero/invoices/${invoiceId}`, { params: { void_in_xero: voidInXero } }),
  markPaidManual: (invoiceId, paymentMethod) => api.post(`/invoices/${invoiceId}/mark-paid`, { payment_method: paymentMethod }),
  searchInvoices: (q) => api.get('/xero/invoices/search', { params: { q, limit: 25 } }),
  linkInvoice: (customerId, data) => api.post(`/customers/${customerId}/xero/link-invoice`, data),
  searchQuotes: (q) => api.get('/xero/quotes/search', { params: { q, limit: 25 } }),
  quoteDraftCustomer: (quoteId) => api.get(`/xero/quotes/${quoteId}/draft-customer`),
};

// Stripe Payment Link reconciliation API
export const stripeReconcileAPI = {
  scan: () => api.post('/stripe-reconcile/scan'),
  pending: () => api.get('/stripe-reconcile/pending'),
  history: () => api.get('/stripe-reconcile/history'),
  customerOpenInvoices: (customerId) => api.get(`/stripe-reconcile/customer-open-invoices/${customerId}`),
  confirm: (pendingId, data) => api.post(`/stripe-reconcile/${pendingId}/confirm`, data),
  dismiss: (pendingId) => api.post(`/stripe-reconcile/${pendingId}/dismiss`),
  getSettings: () => api.get('/stripe-reconcile/settings'),
  saveSettings: (data) => api.post('/stripe-reconcile/settings', data),
};

// Notifications API
export const notificationsAPI = {
  notifyAdmin: (data) => api.post('/notifications/admin', data),
  getAll: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
};

// Engineer Portal API
export const engineerAPI = {
  getMyInstallations: () => api.get('/engineer/my-installations'),
  getCustomer: (customerId) => api.get(`/engineer/customer/${customerId}`),
  timeClockStatus: () => api.get('/engineer/time-clock/status'),
  timeClockPolicy: () => api.get('/engineer/time-clock/policy'),
  clockIn: (data = {}) => api.post('/engineer/time-clock/clock-in', data),
  clockOut: (data = {}) => api.post('/engineer/time-clock/clock-out', data),
  timeClockHistory: (days = 14) => api.get('/engineer/time-clock/history', { params: { days } }),
  updateShiftAllocations: (shiftId, allocations) =>
    api.patch(`/engineer/time-clock/shifts/${shiftId}/allocations`, { job_allocations: allocations }),
  scheduledJobs: () => api.get('/engineer/scheduled-jobs'),
};

// Pipeline Automation API
export const automationAPI = {
  getRules: () => api.get('/pipeline-automation/rules'),
  createRule: (data) => api.post('/pipeline-automation/rules', data),
  updateRule: (id, data) => api.put(`/pipeline-automation/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/pipeline-automation/rules/${id}`),
  getLogs: (limit = 50) => api.get('/pipeline-automation/logs', { params: { limit } }),
  runNow: () => api.post('/pipeline-automation/run-now'),
};

// Social Messaging API
export const messagingAPI = {
  getConversations: (params) => api.get('/messaging/conversations', { params }),
  getMessages: (conversationId, params) => api.get(`/messaging/conversations/${conversationId}/messages`, { params }),
  sendMessage: (data) => api.post('/messaging/send', data),
  markRead: (conversationId) => api.post(`/messaging/conversations/${conversationId}/read`),
  linkCustomer: (conversationId, data) => api.post(`/messaging/conversations/${conversationId}/link-customer`, data),
  getStats: () => api.get('/messaging/stats'),
  sync: () => api.post('/messaging/sync'),
  getSettings: () => api.get('/messaging/settings'),
  saveSettings: (data) => api.post('/messaging/settings', data),
};

// Wix Leads API
export const wixLeadsAPI = {
  getLeads: (params) => api.get('/wix-leads', { params }),
  getLead: (id) => api.get(`/wix-leads/${id}`),
  getStats: () => api.get('/wix-leads/stats'),
  sync: () => api.post('/wix-leads/sync'),
  convert: (id) => api.post(`/wix-leads/${id}/convert`),
  deleteLead: (id) => api.delete(`/wix-leads/${id}`),
  getForms: () => api.get('/wix-leads/forms/list'),
};

// DNO Applications API (ENA Connect Direct)
export const dnoAPI = {
  status: () => api.get('/dno/status'),
  mpanLookup: (params) => api.get('/dno/mpan-lookup', { params }),
  getComms: (customerId) => api.get(`/customers/${customerId}/dno/comms`),
  getSchema: (lcts, showDetails = false) => api.get('/dno/schema', { params: { lcts: lcts.join(','), show_details: showDetails } }),
  resolveApplicationClass: (lcts) => api.get('/dno/resolve-application-class', { params: { lcts: lcts.join(',') } }),
  setCustomerManualStatus: (customerId, status, note = '', extra = {}) =>
    api.patch(`/customers/${customerId}/dno-manual-status`, { status, note, ...extra }),
  generateSld: (customerId, payload = {}) =>
    api.post(`/customers/${customerId}/dno/generate-sld`, payload),
  suggestSldNotes: (customerId, payload = {}) =>
    api.post(`/customers/${customerId}/dno/sld-notes`, payload),
  uploadSld: (customerId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/customers/${customerId}/dno/upload-sld`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  boundaryMapProviders: () => api.get('/dno/boundary-map/providers'),
  getBoundaryMap: (customerId) => api.get(`/customers/${customerId}/dno/boundary-map`),
  saveBoundaryMap: (customerId, payload) => api.post(`/customers/${customerId}/dno/boundary-map`, payload),
  geocodePostcode: (postcode) => api.get(`/monitoring/geocode/${encodeURIComponent(postcode)}`),
  listApplications: (params = {}, body = {}) => api.post('/dno/applications/list', body, { params }),
  createApplication: (payload, rejectDuplicate = false) => api.post('/dno/applications/create', payload, { params: { reject_duplicate: rejectDuplicate } }),
  amendApplication: (appId, payload) => api.post(`/dno/applications/${appId}/amend`, payload),
  addComment: (appId, comment) => api.post(`/dno/applications/${appId}/comment`, { comment }),
  uploadAttachment: (file, fileType = 'image') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/dno/upload-attachment?file_type=${fileType}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getAttachment: (appId, attachmentId) => api.get(`/dno/applications/${appId}/attachment`, { params: { attachment_id: attachmentId } }),
  getRelatedEmails: (appId) => api.get(`/dno/applications/${appId}/related-emails`),
  launchCommissioning: (appId) => api.get(`/dno/commissioning/${appId}/launch`),
  submitCommissioning: (payload) => api.post('/dno/commissioning/submit', payload),
  searchDevices: (params) => api.get('/dno/devices/search', { params }),
  getDeviceReference: () => api.get('/dno/devices/reference'),
  getDeviceDetail: (sysRef) => api.get(`/dno/devices/${encodeURIComponent(sysRef)}`),
  listDrafts: () => api.get('/dno/drafts'),
  saveDraft: (payload) => api.post('/dno/drafts', payload),
  deleteDraft: (id) => api.delete(`/dno/drafts/${id}`),
  listLocalApplications: () => api.get('/dno/local-applications'),
  syncStatuses: () => api.post('/dno/sync-statuses'),
  listFailedApplications: () => api.get('/dno/failed-applications'),
  deleteFailedApplication: (id) => api.delete(`/dno/failed-applications/${id}`),
  retryFailedApplication: (id) => api.post(`/dno/failed-applications/${id}/retry`),
};

// Monitoring Device Links
export const deviceLinksAPI = {
  getAll: () => api.get('/monitoring/device-links'),
  link: (sn, customerId) => api.post('/monitoring/device-links', { sn, customer_id: customerId }),
  unlink: (sn) => api.delete(`/monitoring/device-links/${sn}`),
};


// Saved Views API (per-user filter views)
export const savedViewsAPI = {
  list: (context) => api.get('/users/me/saved-views', { params: { context } }),
  create: (data) => api.post('/users/me/saved-views', data),
  remove: (id) => api.delete(`/users/me/saved-views/${id}`),
};

// Admin reports
export const adminReportsAPI = {
  billingSummary: () => api.get('/admin/billing/summary'),
};

// Variation signing (admin actions live on /api/customers/...; portal & public have their own paths)
export const variationsAPI = {
  adminGet: (customerId, docId) => api.get(`/customers/${customerId}/variations/${docId}`),
  createShareLink: (customerId, docId, sendEmail = false) =>
    api.post(`/customers/${customerId}/variations/${docId}/share-link`, { send_email: sendEmail }),
  createShareLinkAdvanced: (customerId, docId, opts = {}) =>
    api.post(`/customers/${customerId}/variations/${docId}/share-link`, {
      send_email: !!opts.send_email,
      template_trigger: opts.template_trigger || null,
      subject: opts.subject || null,
      body_html: opts.body_html || null,
    }),
  reminderStatus: () => api.get('/admin/variations/sign-reminders/status'),
  reminderRunNow: () => api.post('/admin/variations/sign-reminders/run-now'),
  // Portal
  portalList: () => api.get('/portal/variations'),
  portalSign: (docId, data) => api.post(`/portal/variations/${docId}/sign`, data),
};

export default api;
